import firebase from 'firebase/app';
import { Component, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { SubSink } from 'subsink';
import { Router, RouterEvent, NavigationEnd } from '@angular/router';
import { Observable, fromEvent, merge, of } from 'rxjs';
import { mapTo } from 'rxjs/operators';
import { filter } from 'rxjs/operators';
import { datadogRum } from '@datadog/browser-rum';
import { environment } from '../environments/environment';

import { SharedService } from './modules/shared/services/shared.service';
import { AuthService, AuthUser } from '@monorepo/ui/auth';
import { StateType } from '@monorepo/ui';

interface MenuItem {
  name: string;
  link: string;
  icon: string;
}

@Component({
  selector: 'signgaze-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  themeColors: any = {
    primary: '#000',
    secondary: ' #666',
  };
  changingValue = 50;
  online$: Observable<boolean>;
  StateTypeEnum: any = StateType;
  isConnected = true;
  isloggedInUser: boolean;
  subSink: SubSink = new SubSink();
  isDashboard = false;
  isShowFooter = true;
  isShowListings = false;
  public innerHeight: any;
  currentBookedScreens$: Observable<Screen[]>;
  public isOnline: boolean = navigator.onLine;

  profileItems: MenuItem[] = [
    { name: 'Dashboard', link: 'dashboard', icon: 'table' },
    { name: 'Bookings', link: 'bookings', icon: 'calendar outline' },
    { name: 'Ads', link: 'ads', icon: 'bullhorn' },
    { name: 'My Campaigns', link: 'campaigns', icon: 'clipboard outline' },
    { name: 'My Screens', link: 'screens', icon: 'window maximize outline' },
    { name: 'Finances', link: 'finances', icon: 'dollar sign' },
  ];

  helpItems: MenuItem[] = [
    { name: 'Settings', link: 'settings/user', icon: 'setting' },
    { name: 'Support', link: 'support', icon: 'user outline' },
  ];

  mobileMenuItems: any = [
    { name: 'Home', link: '', icon: 'home' },
    { name: 'Explore', link: 'listings', icon: 'search' },
    { name: 'My Cart', link: 'checkout', icon: 'shopping bag' },
    { name: 'Menu', link: 'profile', icon: 'hamburger' },
  ];

  mobileMenuGuestUser: any = [
    { name: 'Explore', link: 'listings', icon: 'search' },
    { name: 'Login', link: 'login', icon: 'user circle outline' },
  ];

  isLoginPage = true;
  user: firebase.User;
  constructor(
    private router: Router,
    private elementRef: ElementRef,
    public auth: AuthService,
    private sharedService: SharedService
  ) {
    this.online$ = merge(
      of(navigator.onLine),
      fromEvent(window, 'online').pipe(mapTo(true)),
      fromEvent(window, 'offline').pipe(mapTo(false))
    );
  }

  ngOnInit(): void {
    this.checkIfItIsDashboard();
    this.setInnerHeight();
    this.getUser();
    this.getCurrentBookedScreens();
    this.initDataDog();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  private initDataDog(): void {
    datadogRum.init(environment.datadog);
  }

  private getCurrentBookedScreens(): void {
    this.currentBookedScreens$ = this.sharedService.getCurrentBookedScreens();
  }

  private setUserTokenForAlogolia(): void {
    (window as any).aa('getUserToken', null, (err, _algoliaUserToken) => {
      if (err) {
        console.error(err);
        return;
      }
    });
  }

  private checkIfItIsDashboard(): void {
    this.subSink.sink = this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = event.url.split('/');
        const id = url[url.length - 1];
        if (event.url.match('/listings')?.index === 0) {
          this.isShowListings = true;
        }
        if (
          event.url === '/screen-manager' ||
          event.url === '/screen-manager/edit/' + id
        ) {
          this.isShowFooter = false;
        } else {
          this.isShowFooter = true;
        }

        if (
          event.url === '/' ||
          event.url === '/how-it-works' ||
          event.url === '/responsible-hosting' ||
          event.url === '/accessibility' ||
          event.url === '/signgaze-stories' ||
          event.url === '/screen-support' ||
          event.url === '/trust' ||
          event.url === '/presence' ||
          event.url === '/help' ||
          event.url === '/offers' ||
          event.url === '/help/detail' ||
          event.url === '/cancellation' ||
          event.url === '/updates' ||
          event.url === '/invite' ||
          event.url === '/about' ||
          event.url === '/screen-detail' ||
          event.url === '/career' ||
          event.url === '/career/detail' ||
          event.url === '/blogs' ||
          event.url === '/contact' ||
          event.url === '/privacy' ||
          event.url === '/terms-condition' ||
          event.url === '/blogs/detail' ||
          event.url === '/landing' ||
          event.url === '/location-manager' ||
          event.url === '/screen-manager' ||
          event.url === '/screen-manager/edit/' + id ||
          event.url.match('/location-manager/' + id)?.index === 0 ||
          event.url.match('/checkout')?.index === 0 ||
          event.url.match('/listing')?.index === 0 ||
          event.url.match('/admin')?.index === 0
        ) {
          this.isDashboard = false;
        } else {
          this.isDashboard = true;
        }
        event.url === '/login' || event.url === '/auth/email/' + id
          ? (this.isLoginPage = true)
          : (this.isLoginPage = false);
      });
  }

  public setInnerHeight(): void {
    this.innerHeight = window.innerHeight;
    this.elementRef.nativeElement.style.setProperty(
      '--innerHeight',
      `${innerHeight}px`
    );
  }

  public getUser(): void {
    this.subSink.sink = this.auth.user.subscribe({
      next: (user: AuthUser): void => {
        if (user) {
          this.isloggedInUser = true;
          this.setUserTokenForAlogolia();
        }
      },
    });
  }
}
